import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useActiveMunicipio } from '@/context/ActiveMunicipioContext';
import { supabase } from '@/lib/supabase';

let cachedEmails: string[] | null = null;
let cacheExpiry = 0;

async function fetchFormacaoOnlyEmails(): Promise<string[]> {
  if (cachedEmails !== null && Date.now() < cacheExpiry) {
    return cachedEmails;
  }
  try {
    const { data } = await supabase
      .from('app_settings')
      .select('value')
      .eq('key', 'formacao_only_emails')
      .maybeSingle();
    const list = data?.value
      ? data.value.split(',').map((e: string) => e.trim().toLowerCase()).filter(Boolean)
      : [];
    cachedEmails = list;
    cacheExpiry = Date.now() + 5 * 60 * 1000;
    return list;
  } catch {
    cachedEmails = [];
    return [];
  }
}

/**
 * Hook que redireciona automaticamente para /formacao quando:
 * - O módulo ativo é Gestão da Formação, OU
 * - O email do utilizador está na lista restrita de formação
 *
 * Corre apenas uma vez por sessão (quando o perfil e municípios estão prontos).
 * Não causa re-renders nem recarregamentos nas navegações subsequentes.
 */
export function useFormacaoRedirect() {
  const { profile, profileLoading } = useAuth();
  const { activeMunicipio, loadingMunicipios } = useActiveMunicipio();
  const navigate = useNavigate();
  const location = useLocation();
  const hasRedirected = useRef(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (hasRedirected.current) return;
    if (profileLoading || loadingMunicipios) return;
    if (!profile) return;

    const checkAndRedirect = async () => {
      const formacaoOnlyEmails = await fetchFormacaoOnlyEmails();
      const isFormacaoModule =
        activeMunicipio?.sigla === 'GF' ||
        activeMunicipio?.nome === 'Gestão da Formação';
      const isRestrictedEmail = formacaoOnlyEmails.includes(profile.email.toLowerCase());

      if (isRestrictedEmail || isFormacaoModule) {
        // Só redireciona se NÃO estiver já numa rota de /formacao
        if (!location.pathname.startsWith('/formacao')) {
          hasRedirected.current = true;
          navigate('/formacao', { replace: true });
        }
      }
      setReady(true);
    };

    checkAndRedirect();
  }, [profile, profileLoading, activeMunicipio, loadingMunicipios, navigate, location.pathname]);

  return ready;
}